import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { BUSINESS, PAGE_META } from '../constants/business';

interface ServiceContent {
  title: string;
  intro: string[];
  whoItsFor: string[];
  process: { step: string; detail: string }[];
  faqs: { question: string; answer: string }[];
}

const serviceContent: Record<string, ServiceContent> = {
  'residential-closings': {
    title: 'Residential Closings in Woodbridge, NJ',
    intro: [
      'A residential real estate closing is the final step in your home purchase or sale — the moment ownership officially transfers. At M.A.D. Title Agency, we coordinate every detail so you can focus on what matters: moving into your new home.',
      'From first-time buyers in Woodbridge to seasoned sellers across Middlesex County, we handle the title search, document preparation, escrow management, and settlement with precision and care.',
    ],
    whoItsFor: [
      'First-time homebuyers navigating the closing process for the first time',
      'Sellers looking for a smooth, on-time closing with no surprises',
      'Real estate agents who need a reliable title partner for their clients',
      'Attorneys coordinating closings across multiple New Jersey counties',
      'Lenders requiring thorough title work and timely policy issuance',
    ],
    process: [
      { step: 'Order & Review', detail: 'We receive the contract and open the title order, reviewing all transaction details for accuracy.' },
      { step: 'Title Search', detail: 'Our team conducts a comprehensive search of public records to verify ownership, liens, judgments, and encumbrances.' },
      { step: 'Title Commitment', detail: 'We issue a title commitment outlining the conditions under which we will insure the property.' },
      { step: 'Document Preparation', detail: 'All closing documents are prepared, reviewed, and organized for the settlement table.' },
      { step: 'Settlement', detail: 'We conduct the closing, ensuring all documents are properly executed, funds are disbursed, and the deed is recorded.' },
      { step: 'Post-Closing', detail: 'Final title policies are issued and recorded documents are returned to all parties.' },
    ],
    faqs: [
      {
        question: 'How long does a typical residential closing take?',
        answer: 'Most residential closings in New Jersey take 30-45 days from contract to close. We can accommodate rush closings when needed, often completing the process in as few as 10-14 days with all parties cooperating.',
      },
      {
        question: 'What costs should I expect at closing?',
        answer: 'Closing costs typically include title insurance premiums, search fees, recording fees, and settlement charges. In New Jersey, these usually range from 2-5% of the purchase price. We provide a detailed estimate upfront so there are no surprises.',
      },
      {
        question: 'Can you handle closings at a location of my choice?',
        answer: 'Yes. While many closings take place at our Woodbridge office, we regularly conduct closings at attorney offices, real estate offices, or other convenient locations throughout New Jersey.',
      },
    ],
  },
  'commercial-closings': {
    title: 'Commercial Real Estate Closings in NJ',
    intro: [
      'Commercial real estate transactions demand a higher level of scrutiny, coordination, and expertise. M.A.D. Title Agency brings decades of experience to commercial closings — from multi-unit residential complexes to retail and industrial properties across New Jersey.',
      'We work directly with investors, developers, lenders, and attorneys to ensure every commercial transaction closes on time with clean title and full compliance.',
    ],
    whoItsFor: [
      'Commercial property investors acquiring or disposing of assets',
      'Real estate developers closing on land or development projects',
      'Commercial lenders requiring thorough due diligence and title assurance',
      'Business owners purchasing or selling commercial space',
      'Property management companies handling portfolio transactions',
    ],
    process: [
      { step: 'Engagement & Scope', detail: 'We review the transaction structure, identify all parties, and outline the scope of title work required.' },
      { step: 'Commercial Title Search', detail: 'Enhanced title examination including entity searches, UCC filings, environmental liens, and municipal compliance verification.' },
      { step: 'Due Diligence Support', detail: 'We provide survey review, zoning verification, and coordinate with attorneys on complex title issues.' },
      { step: 'Title Commitment & Endorsements', detail: 'Issuance of commercial title commitment with applicable endorsements for the transaction type.' },
      { step: 'Closing Coordination', detail: 'We manage the closing process, including multi-party coordination, wire transfers, and document execution.' },
      { step: 'Post-Closing & Recording', detail: 'All documents are recorded, final policies issued, and closing packages distributed to all parties.' },
    ],
    faqs: [
      {
        question: 'How is a commercial closing different from residential?',
        answer: 'Commercial closings typically involve more complex title searches, entity verification, UCC searches, environmental reviews, and specialized endorsements. They often require coordination among more parties and may involve larger sums requiring enhanced security protocols.',
      },
      {
        question: 'Can you handle 1031 exchange closings?',
        answer: 'Yes. We regularly coordinate with qualified intermediaries to facilitate 1031 tax-deferred exchanges, ensuring all timeline and documentation requirements are met for both the relinquished and replacement properties.',
      },
      {
        question: 'What is your typical turnaround for commercial title work?',
        answer: 'Standard commercial title searches take 3-5 business days depending on complexity. We offer expedited service for time-sensitive transactions and can often deliver preliminary results within 48 hours.',
      },
    ],
  },
  'title-insurance': {
    title: 'Title Insurance in NJ',
    intro: [
      "Title insurance is a one-time purchase that protects your ownership rights and financial investment in real estate for as long as you own the property. Unlike other insurance that protects against future events, title insurance protects against problems that already exist — but haven't been discovered yet.",
      "At M.A.D. Title Agency, we issue both owner's and lender's title insurance policies through top-rated national underwriters, giving you peace of mind that your New Jersey property investment is fully protected.",
    ],
    whoItsFor: [
      'Homebuyers who want to protect their largest financial investment',
      'Property owners seeking coverage against undiscovered title defects',
      'Mortgage lenders who require title insurance as a condition of the loan',
      'Real estate investors building portfolios across New Jersey',
      'Attorneys advising clients on title protection options',
    ],
    process: [
      { step: 'Title Search', detail: 'We conduct a thorough examination of public records to identify any issues affecting the property title.' },
      { step: 'Issue Resolution', detail: 'Any title defects, liens, or encumbrances discovered are addressed and resolved before closing.' },
      { step: 'Commitment Issuance', detail: 'A title commitment is issued detailing the terms and conditions under which the policy will be written.' },
      { step: 'Policy Selection', detail: "We help you understand the difference between owner's and lender's policies and recommend appropriate coverage." },
      { step: 'Policy Issuance', detail: 'After closing, your final title insurance policy is issued, providing ongoing protection for your investment.' },
    ],
    faqs: [
      {
        question: "What's the difference between owner's and lender's title insurance?",
        answer: "Owner's title insurance protects your equity and ownership rights for as long as you own the property. Lender's title insurance protects only the mortgage lender's interest and decreases as you pay down the loan. We strongly recommend both for complete protection.",
      },
      {
        question: 'How much does title insurance cost in New Jersey?',
        answer: 'Title insurance premiums in New Jersey are regulated by the state Department of Banking and Insurance. The one-time premium is based on the property purchase price or loan amount. Contact us for a specific quote — we are happy to provide exact figures for your transaction.',
      },
      {
        question: 'Is title insurance really necessary?',
        answer: "While lender's title insurance is required for most mortgages, owner's title insurance is optional but highly recommended. It protects against hidden risks like forged documents, undisclosed heirs, recording errors, and boundary disputes that can threaten your ownership even years after purchase.",
      },
    ],
  },
  'title-search': {
    title: 'Title Search & Examination',
    intro: [
      "A title search is a detailed examination of public records related to a property's ownership history. It reveals liens, judgments, easements, encumbrances, and any other issues that could affect your right to own or use the property.",
      "M.A.D. Title Agency's experienced examiners combine digital database access with traditional courthouse research to deliver thorough, accurate title searches — typically within 24-48 hours.",
    ],
    whoItsFor: [
      'Homebuyers and sellers preparing for a real estate transaction',
      'Real estate attorneys conducting due diligence for their clients',
      'Lenders requiring title examination as part of the underwriting process',
      'Property owners resolving boundary disputes or ownership questions',
      'Investors evaluating properties for acquisition',
    ],
    process: [
      { step: 'Order Intake', detail: 'We receive the property details and determine the appropriate search scope based on the transaction type.' },
      { step: 'Records Research', detail: 'Our examiners search deed records, mortgage records, court records, tax records, and municipal liens.' },
      { step: 'Chain of Title', detail: 'We trace the complete ownership history to verify an unbroken chain of title from the current owner back through prior conveyances.' },
      { step: 'Issue Identification', detail: 'Any liens, judgments, easements, or encumbrances affecting the property are documented and flagged.' },
      { step: 'Report Delivery', detail: 'A comprehensive title report is delivered detailing all findings, with recommendations for resolving any issues discovered.' },
    ],
    faqs: [
      {
        question: 'How long does a title search take?',
        answer: 'Most standard title searches are completed within 24-48 hours. Complex properties — such as those with extensive ownership history, multiple parcels, or properties in municipalities with limited digital records — may require additional time. Rush service is available.',
      },
      {
        question: 'What types of issues can a title search uncover?',
        answer: 'Common findings include outstanding mortgages, tax liens, judgment liens, mechanic\'s liens, easements, restrictive covenants, boundary discrepancies, and errors in prior recordings. Our job is to find and help resolve these before closing.',
      },
      {
        question: 'What happens if a problem is found during the title search?',
        answer: 'We work with all parties — sellers, attorneys, lenders — to resolve title issues before closing. Many issues can be cleared with proper documentation. For more complex problems, we outline options and coordinate the resolution process.',
      },
    ],
  },
  'refinance': {
    title: 'Refinance Closings in NJ',
    intro: [
      'Refinancing your mortgage can save you thousands over the life of your loan — but it requires the same title diligence as the original purchase. M.A.D. Title Agency provides fast, accurate refinance title services coordinated directly with your lender for a seamless experience.',
      'Whether you\'re lowering your rate, switching from adjustable to fixed, or tapping equity, we handle the title search, insurance, and closing so your refinance closes on schedule.',
    ],
    whoItsFor: [
      'Homeowners refinancing for a lower interest rate',
      'Borrowers converting from adjustable-rate to fixed-rate mortgages',
      'Homeowners accessing equity through cash-out refinancing',
      'Mortgage lenders and brokers needing reliable refinance title services',
      'Attorneys facilitating refinance transactions for their clients',
    ],
    process: [
      { step: 'Lender Order', detail: 'We receive the refinance order from your lender and begin the title update process immediately.' },
      { step: 'Title Update Search', detail: 'We search for any new liens, judgments, or encumbrances since the original policy was issued.' },
      { step: 'Payoff Coordination', detail: 'We obtain payoff statements from existing lenders and verify all amounts to be satisfied at closing.' },
      { step: 'Document Preparation', detail: 'Closing documents are prepared per your lender\'s instructions and reviewed for accuracy.' },
      { step: 'Closing', detail: 'We conduct the refinance closing at your convenience — often at your home or our office.' },
      { step: 'Disbursement & Recording', detail: 'Existing mortgages are paid off, the new mortgage is recorded, and your lender\'s title policy is issued.' },
    ],
    faqs: [
      {
        question: 'Do I need a new title search for a refinance?',
        answer: 'Yes. Even though your property was searched when you originally purchased it, a new search is required to check for any liens, judgments, or issues that may have arisen since then. The good news is refinance searches are typically faster and less expensive than full purchase searches.',
      },
      {
        question: 'How quickly can you close a refinance?',
        answer: 'We can typically complete refinance title work within 3-5 business days and schedule the closing at your convenience. Many refinances close within 2-3 weeks of the lender placing the order. We prioritize meeting your lender\'s lock expiration dates.',
      },
      {
        question: 'Will I need new title insurance for a refinance?',
        answer: "A new lender's title insurance policy is required for most refinances. However, you may be eligible for a reissue rate (discount) if the existing policy is still available. Your owner's policy from the original purchase remains in effect — no new owner's policy is needed.",
      },
    ],
  },
};

const ServiceDetail: React.FC = () => {
  const { serviceSlug } = useParams<{ serviceSlug: string }>();
  const content = serviceSlug ? serviceContent[serviceSlug] : undefined;
  const metaKey = `/services/${serviceSlug}` as string;
  const meta = PAGE_META[metaKey];

  if (!content) {
    return <Navigate to="/services" replace />;
  }

  return (
    <>
      <Helmet>
        <title>{meta?.title || content.title}</title>
        <meta name="description" content={meta?.description || content.intro[0]} />
        <meta property="og:title" content={meta?.title || content.title} />
        <meta property="og:description" content={meta?.description || content.intro[0]} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={BUSINESS.displayName} />
        <link rel="canonical" href={`${BUSINESS.siteUrl}/services/${serviceSlug}`} />
      </Helmet>

      {/* H1 Hero */}
      <Box
        sx={{
          backgroundColor: '#1e3a8a',
          color: 'white',
          py: { xs: 6, md: 8 },
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h1"
            component="h1"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '2rem', md: '3rem' },
              textAlign: 'center',
              color: 'white',
            }}
          >
            {content.title}
          </Typography>
        </Container>
      </Box>

      {/* Intro */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        {content.intro.map((paragraph, i) => (
          <Typography
            key={i}
            variant="body1"
            paragraph
            sx={{ fontSize: '1.1rem', lineHeight: 1.7, color: '#374151', maxWidth: 800 }}
          >
            {paragraph}
          </Typography>
        ))}
      </Container>

      {/* Who It's For */}
      <Box sx={{ backgroundColor: '#f8fafc', py: 6 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" sx={{ mb: 4, color: '#1e3a8a', fontWeight: 600 }}>
            Who This Is For
          </Typography>
          <List>
            {content.whoItsFor.map((item, i) => (
              <ListItem key={i} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <CheckIcon sx={{ color: '#f59e0b' }} />
                </ListItemIcon>
                <ListItemText
                  primary={item}
                  primaryTypographyProps={{ sx: { fontSize: '1.05rem', color: '#374151' } }}
                />
              </ListItem>
            ))}
          </List>
        </Container>
      </Box>

      {/* Our Process */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography variant="h3" component="h2" sx={{ mb: 4, color: '#1e3a8a', fontWeight: 600 }}>
          Our Process
        </Typography>
        <Grid container spacing={3}>
          {content.process.map((step, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Card
                sx={{
                  height: '100%',
                  p: 3,
                  border: '1px solid #e2e8f0',
                  borderRadius: 2,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mb: 1,
                    color: '#f59e0b',
                    fontWeight: 700,
                    fontSize: '0.875rem',
                  }}
                >
                  STEP {i + 1}
                </Typography>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: '#1f2937' }}>
                  {step.step}
                </Typography>
                <Typography variant="body2" sx={{ color: '#4b5563', lineHeight: 1.6 }}>
                  {step.detail}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* FAQs */}
      <Box sx={{ backgroundColor: '#f8fafc', py: 6 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" sx={{ mb: 4, color: '#1e3a8a', fontWeight: 600 }}>
            Frequently Asked Questions
          </Typography>
          {content.faqs.map((faq, i) => (
            <Paper
              key={i}
              elevation={1}
              sx={{
                p: 4,
                mb: 3,
                border: '1px solid #e2e8f0',
                borderRadius: 2,
              }}
            >
              <Typography variant="h6" sx={{ mb: 2, color: '#1e3a8a', fontWeight: 600 }}>
                {faq.question}
              </Typography>
              <Typography variant="body1" sx={{ lineHeight: 1.7, color: '#374151' }}>
                {faq.answer}
              </Typography>
            </Paper>
          ))}
        </Container>
      </Box>

      {/* CTA Block */}
      <Box
        sx={{
          py: 8,
          textAlign: 'center',
          background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
          color: 'white',
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h3" component="h2" sx={{ mb: 2, fontWeight: 700, color: 'white' }}>
            Ready to Get Started?
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
            Contact {BUSINESS.displayName} today for a free quote on your transaction.
          </Typography>
          <Button
            variant="contained"
            size="large"
            component={Link}
            to="/contact"
            endIcon={<ArrowIcon />}
            sx={{
              backgroundColor: '#f59e0b',
              color: '#1f2937',
              px: 5,
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#d97706',
              },
            }}
          >
            Start Your Closing
          </Button>
          <Typography variant="body2" sx={{ mt: 3, opacity: 0.8 }}>
            Or call us at{' '}
            <a href={`tel:${BUSINESS.contact.phone}`} style={{ color: 'white' }}>
              {BUSINESS.contact.phoneDisplay}
            </a>
          </Typography>
        </Container>
      </Box>
    </>
  );
};

export default ServiceDetail;
